import type * as Theme from '#theme'
import type { Image } from '../types/media'
import { resolveThemeAssetImage } from '../theme/utils/resolveThemeAsset'
import { themeAssetUrls } from '#theme-assets'
import { computed, useCoreConfig, useTheme } from '#imports'
import type { ComputedRef } from '#imports'

export const LOGO_QUALITY = 100

/**
 * Resolves a brand logo for the given slot.
 *
 * Resolution: `site.logo[slot]` (consumer override in nuxt.config.ts)
 * falls back to `theme.logo[slot]` (brand default shipped with the theme).
 * Whichever wins, its `dark` counterpart replaces it while a dark theme is
 * active — both brands keep their logo in `site.logo`, so a dark wordmark
 * has to be expressible there and not only on the theme.
 *
 * Theme-shipped logos are declared with brand-relative keys
 * (e.g. `spilnu/logo/primary.png`) and resolved to public URLs via the
 * generated `#theme-assets` map. Site overrides that already look
 * like URLs (absolute, `http(s)://`, `data:`) pass through unchanged.
 *
 * `quality` defaults to `LOGO_QUALITY` (100). A theme slot or a `site.logo` override may
 * set its own. Pass it on with `v-bind="logo"`, or read `logo.quality` when only `src` is
 * bound.
 *
 * @example
 * const logo = useThemeLogo('icon')
 * // -> ComputedRef<Image | undefined>
 * // <NuxtImg v-bind="logo" />  or  <NuxtImg :src="logo?.src" :quality="logo?.quality" />
 */
export function useThemeLogo(slot: Theme.LogoSlot): ComputedRef<Image | undefined> {
    const config = useCoreConfig()
    const { theme } = useTheme()

    return computed(() => {
        const raw = config.site.logo?.[slot] ?? theme.value.logo?.[slot]

        // A dark counterpart only replaces the image, so a slot that omits width
        // or height on it keeps the light one's dimensions and the layout holds.
        const resolved = theme.value.dark && raw?.dark ? { ...raw, ...raw.dark } : raw

        const image = resolveThemeAssetImage(resolved, themeAssetUrls)

        return image && { quality: LOGO_QUALITY, ...image }
    })
}
